import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { showToast } from 'shared/ui'
import { VoiceServicesApi } from 'entities/Compliance/api/voiceServicesApi'
import { type Cnam } from 'entities/Compliance/model/OrganizationCnam/Cnam'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { CommonNumbersStore } from 'pages/settings/pages/compliance/ui/CommonNumbers/store/commonNumbersStore'
import { SettingsPath } from 'pages/settings/route/settingsPath'
import { type CallingPageStore } from 'pages/settings/pages/compliance/pages/calling/store/callingPageStore'

export class EditCnamNumbersStore {
  private readonly _numbersStore: CommonNumbersStore
  private _editingCnam: Cnam | null = null
  private _loading = false
  private _cnamNumbersLoading = false
  private _originalNumbers: number[] = []

  constructor(private _entities: IEntitiesStore, private _callingPageStore: CallingPageStore) {
    makeAutoObservable(this)

    this._numbersStore = new CommonNumbersStore(
      {
        setLoading: this.setLoading,
      },
      this._entities
    )

    this.init()
  }

  get numbersStore() {
    return this._numbersStore
  }

  get loading() {
    return this._loading || this._cnamNumbersLoading
  }

  get cnamNumbersLoading() {
    return this._cnamNumbersLoading
  }

  get freeNumbers() {
    return this._entities.complianceStore.complianceVoiceServicesStore.freeNumbers
  }

  get unassignedFreeNumbers() {
    return this.freeNumbers.filter((number) => !number.numberable?.name)
  }

  get assignedFreeNumbers() {
    return this.freeNumbers.filter((number) => number.numberable?.name)
  }

  get editingCnam() {
    return this._editingCnam
  }

  get isDisabledSubmit() {
    return this.loading
  }

  get hasUnsavedChanges() {
    const currentSelectedNumbers = this.numbersStore.multiSelectedKeys
    const currentNumbersSet = new Set(currentSelectedNumbers)
    const originalNumbersSet = new Set(this._originalNumbers)

    if (currentNumbersSet.size !== originalNumbersSet.size) {
      return true
    }

    for (const number of currentNumbersSet) {
      if (!originalNumbersSet.has(number)) {
        return true
      }
    }

    for (const number of originalNumbersSet) {
      if (!currentNumbersSet.has(number)) {
        return true
      }
    }

    return false
  }

  setLoading = (value: boolean) => {
    this._loading = value
  }

  setEditingCnam = (cnam: Cnam | null) => {
    this._editingCnam = cnam
  }

  closeSetting = () => {
    if (this.hasUnsavedChanges) {
      const modalId = nanoid()

      modalStore.addModal({
        id: modalId,
        type: ModalTypeList.WARNING,
        title: 'You have unsaved changes',
        primaryAction: {
          text: 'Leave without saving',
          onAction: () => {
            this.setEditingCnam(null)
            modalStore.closeModal(modalId)

            uiStore.changeRoute({
              path: `/${SettingsPath.compliance.calling.root}`,
            })
          },
        },
        secondaryAction: {
          text: 'Keep editing',
          onAction: () => {
            modalStore.closeModal(modalId)
          },
        },
      })
      return
    }

    uiStore.changeRoute({
      path: `/${SettingsPath.compliance.calling.root}`,
    })
  }

  setEditingCnamFromId = async (id: number) => {
    const cnam = this._entities.complianceStore.organizationCnam?.getItem(id)

    if (cnam) {
      this.setEditingCnam(cnam)
      this.setLoading(true)

      try {
        runInAction(() => {
          this._cnamNumbersLoading = true
        })
        const numbers = await this._entities.complianceStore.loadCnamNumbers(id)

        this.initNumbersStoreForAdding()

        this._originalNumbers = numbers?.map((number) => number.id) || []

        numbers?.forEach((number) => {
          this.numbersStore.setMultipleItem({
            id: number.id.toString(),
            label: number.number,
            value: number.id.toString(),
            data: number,
          })
        })
      } catch (error) {
        logger.error(error)
      } finally {
        runInAction(() => {
          this._cnamNumbersLoading = false
        })
      }
    }
  }

  initNumbersStoreForAdding = () => {
    this.numbersStore.init({
      unassignedNumbers: this.unassignedFreeNumbers,
      assignedNumbers: this.assignedFreeNumbers,
    })
  }

  init = async () => {
    await this._entities.complianceStore.complianceVoiceServicesStore.loadFreeNumbers()

    this.initNumbersStoreForAdding()
  }

  updateNumbers = async () => {
    if (this._callingPageStore.complianceLayerStore.preventSubmitModal()) {
      return
    }
    if (!this.editingCnam) return

    try {
      this.setLoading(true)

      const currentSelectedNumbers = this.numbersStore.multiSelectedKeys

      const removedNumbers = this._originalNumbers.filter(
        (originalId) => !currentSelectedNumbers.includes(originalId)
      )

      const addedNumbers = currentSelectedNumbers.filter(
        (currentId) => !this._originalNumbers.includes(Number(currentId))
      )

      let numbersLength = this.editingCnam.count || 0

      if (addedNumbers.length > 0) {
        const request = await VoiceServicesApi.addNumbersToCnamCallerId({
          cnam_id: this.editingCnam.id,
          phone_numbers: addedNumbers,
        })

        numbersLength = request.data.length
      }
      if (removedNumbers.length > 0) {
        const request = await VoiceServicesApi.deleteNumbersFromCnamCallerId({
          cnam_id: this.editingCnam.id,
          phone_numbers: removedNumbers,
        })

        numbersLength = request.data.length
      }

      this._entities.complianceStore.complianceVoiceServicesStore.cnamServices?.syncData([
        {
          ...this.editingCnam.origin,
          numbers_count: numbersLength,
        },
      ])

      uiStore.changeRoute({
        path: `/${SettingsPath.compliance.calling.root}`,
      })

      showToast({
        title: `CNAM Caller ID “${this.editingCnam.name}” updated`,
        type: 'success',
      })
      this.setEditingCnam(null)
    } catch (error) {
      logger.error(error)
    } finally {
      this.setLoading(false)
    }
  }
}
